// In-cabinet clip player — «▶ смотреть» from QueuePage (staff, Russian) and
// MySpacePage (contributor, i18n). Same <dialog>+showModal() pattern as
// PasswordDialog/AddDialog: opened via a callback ref (not useEffect — the
// effect fires before Preact attaches the ref, which left the dialog closed),
// guarded with `!el.open` because a callback ref also fires on re-renders and
// showModal() on an already-open dialog throws InvalidStateError.
//
// Every visible string is a prop, same reason as PasswordDialog: this
// component serves two different language surfaces and has no opinion about
// which. srcRaw/srcVoiced already carry whatever auth the worker's file route
// needs (api.captureFileUrl puts the session token in the query — a plain
// <video src> cannot set an Authorization header).
import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api';
import { attachFragmentedPlayer, type AttachedPlayer } from './fragmentedPlayer';

type Props = {
  /** Клип, у которого спрашиваем оглавление. Без него плеер работает как
   *  раньше — обычной ссылкой (и на длинном клипе ждёт минуты). */
  captureId?: string;
  title: string;
  srcRaw: string;
  srcVoiced?: string | null;
  // Only rendered (and only meaningful) when srcVoiced is present — the
  // contributor cabinet never passes a voiced variant, so it can skip these.
  rawLabel?: string;
  voicedLabel?: string;
  closeLabel: string;
  /** «Готовим просмотр…» — пока едет оглавление клипа. */
  preparingLabel?: string;
  onClose: () => void;
};

export function PlayerModal({
  captureId,
  title,
  srcRaw,
  srcVoiced,
  rawLabel = 'raw',
  voicedLabel = 'voiced',
  closeLabel,
  preparingLabel = 'Loading…',
  onClose,
}: Props) {
  const openModal = (el: HTMLDialogElement | null) => {
    if (el && !el.open) el.showModal();
  };
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [variant, setVariant] = useState<'raw' | 'voiced'>('raw');
  // Пока оглавление едет — показываем, что происходит: иначе пустой чёрный
  // прямоугольник неотличим от того самого зависания, ради которого всё это
  // и делалось.
  const [loading, setLoading] = useState(true);
  const attachedRef = useRef<AttachedPlayer | null>(null);

  // Оглавление + MediaSource. Если что-то из этого не получилось (старый
  // браузер, клип залит готовым файлом и индекса в нём нет, кодек не тот) —
  // молча падаем на обычный src: хуже, чем было, не станет.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    attachedRef.current?.detach();
    attachedRef.current = null;

    const fallback = () => {
      const v = videoRef.current;
      if (v && !cancelled) v.src = variant === 'voiced' && srcVoiced ? srcVoiced : srcRaw;
      if (!cancelled) setLoading(false);
    };

    if (!captureId) {
      fallback();
      return;
    }

    void (async () => {
      try {
        const index = await api.captureIndex(captureId, variant);
        const v = videoRef.current;
        if (cancelled || !v) return;
        const fileUrl = variant === 'voiced' && srcVoiced ? srcVoiced : srcRaw;
        const attached = attachFragmentedPlayer({
          video: v,
          index,
          fetchRange: async (offset, length) => {
            const res = await fetch(fileUrl, {
              headers: { Range: `bytes=${offset}-${offset + length - 1}` },
            });
            if (!res.ok && res.status !== 206) throw new Error(`range_failed_${res.status}`);
            return res.arrayBuffer();
          },
          onError: () => {
            // Сбой посреди воспроизведения: отдаём ссылку обычному плееру,
            // человек хотя бы досмотрит.
            attachedRef.current?.detach();
            attachedRef.current = null;
            fallback();
          },
        });
        if (!attached) return fallback();
        attachedRef.current = attached;
        setLoading(false);
      } catch {
        fallback();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [captureId, variant, srcRaw, srcVoiced]);

  useEffect(
    () => () => {
      attachedRef.current?.detach();
      attachedRef.current = null;
    },
    [],
  );

  // Stop the network request and free the decoder before the dialog actually
  // closes — otherwise a 20-minute clip keeps streaming into a detached
  // element ("не оставлять качающийся видеотег").
  const stop = () => {
    attachedRef.current?.detach();
    attachedRef.current = null;
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
  };
  const handleClose = () => {
    stop();
    onClose();
  };

  return (
    <dialog ref={openModal} onClose={handleClose} class="player-modal">
      <div class="player-modal-head">
        <h3>{title}</h3>
        {srcVoiced && (
          <div class="seg">
            <button type="button" class={variant === 'raw' ? 'on' : ''} onClick={() => setVariant('raw')}>
              {rawLabel}
            </button>
            <button type="button" class={variant === 'voiced' ? 'on' : ''} onClick={() => setVariant('voiced')}>
              {voicedLabel}
            </button>
          </div>
        )}
      </div>
      {/* key forces a fresh <video> on variant switch — reusing one element and
          just swapping src left the old buffered range on screen for a beat. */}
      {loading && <div class="player-modal-loading">{preparingLabel}</div>}
      {/* src НЕ атрибутом: им управляет эффект выше — либо MediaSource, либо
          прямая ссылка запасным путём. */}
      <video key={variant} ref={videoRef} controls playsInline class="player-modal-video" />
      <div class="actions">
        <button type="button" class="btn-ghost" onClick={handleClose}>
          {closeLabel}
        </button>
      </div>
    </dialog>
  );
}
